import { useTranslation } from 'react-i18next';

interface Props {
  feedId: string | null;
  jobId: string | null;
  progressPct: number;
}

/**
 * Phase 4 ("Berechnet die Quality Scores") detail panel.
 * Intentionally minimal — the previous live-distribution histogram was
 * confusing because the score job runs feed-wide (not just over the freshly
 * optimized batch), so the bucket counts were always inflated and never
 * felt "live". Now: a clean animated indicator + status text. Score-job
 * progress feeds the thin bar below.
 */
export function Phase4Score({ progressPct }: Props) {
  const { t } = useTranslation('optimization');
  return (
    <div className="bg-muted rounded-lg p-6 mb-4 flex flex-col items-center text-center">
      {/* Large rotating solid-color ring around a calculator/star icon */}
      <div className="phase4-ring-wrap relative w-20 h-20 mb-4">
        <div className="phase4-ring-spin absolute inset-0 rounded-full" />
        {/* Fix-Runde 1: `0 4px 12px` hatte Versatz nach unten, hob also
            wirklich eine Flaeche vom Grund ab (kein Glow) -> shadow-md. */}
        <div className="absolute inset-[6px] rounded-full bg-card shadow-md flex items-center justify-center">
          <span className="text-2xl">📊</span>
        </div>
      </div>

      <div className="text-base font-bold text-foreground mb-1">
        {t('progressModal.phase4.calculating')}
      </div>
      <div className="text-xs text-muted-foreground mb-4 max-w-[320px]">
        {t('progressModal.phase4.measuring')}
      </div>

      <div className="w-full max-w-[320px] h-1 bg-muted rounded-full overflow-hidden relative">
        <div
          className="h-full bg-primary rounded-full"
          style={{
            width: `${Math.max(8, Math.min(100, progressPct))}%`,
            transition: 'width 0.8s cubic-bezier(0.22, 0.61, 0.36, 1)',
          }}
        />
        <div className="absolute inset-0 phase4-shimmer pointer-events-none" />
      </div>

      <style>{`
        @keyframes phase4-spin-kf {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .phase4-ring-spin {
          border: 3px solid hsl(var(--primary) / 0.15);
          border-top-color: hsl(var(--primary));
          animation: phase4-spin-kf 1.6s linear infinite;
        }
        @keyframes phase4-shimmer-kf {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .phase4-shimmer {
          background: linear-gradient(90deg, transparent 0%, hsl(var(--primary) / 0.18) 50%, transparent 100%);
          animation: phase4-shimmer-kf 2s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .phase4-ring-spin, .phase4-shimmer { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
